import { useEffect, useRef } from "react";

import { halloweenModeStore } from "@/stores/halloweenMode";

const BOLT_MS = 450;
const FLASH_MS = 650;

export const HalloweenLightning = () => {
  /* React Hooks */
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let frame = 0;
    const unsubscribe = halloweenModeStore.subscribe((state, previous) => {
      if (!state.isHalloweenMode || previous.isHalloweenMode) return;
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context) return;
      cancelAnimationFrame(frame);
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const randomBetween = (min: number, max: number) =>
        min + Math.random() * (max - min);
      // Match the yard's jagged trunk, fine forks, and cool white glow.
      const points: [number, number][] = [];
      let x = randomBetween(0.15, 0.85) * canvas.width;
      const bottom = randomBetween(0.6, 0.75) * canvas.height;
      for (let y = 0; y < bottom; y += randomBetween(12, 26)) {
        points.push([x, y]);
        x += randomBetween(-18, 18);
      }
      const branches = points
        .filter((_, index) => index > 2 && Math.random() < 0.18)
        .map(([startX, startY]) => {
          const branch: [number, number][] = [[startX, startY]];
          let branchX = startX;
          const direction = Math.random() < 0.5 ? -1 : 1;
          for (let step = 1; step < 5; step += 1) {
            branchX += direction * randomBetween(6, 16);
            branch.push([branchX, startY + step * randomBetween(8, 14)]);
          }
          return branch;
        });
      const started = performance.now();
      const draw = (now: number) => {
        const elapsed = now - started;
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (elapsed >= FLASH_MS) return;
        context.fillStyle = `rgba(232, 238, 255, ${((1 - elapsed / FLASH_MS) * 0.45).toFixed(3)})`;
        context.fillRect(0, 0, canvas.width, canvas.height);
        if (elapsed < BOLT_MS) {
          context.shadowColor = "rgba(200, 215, 255, 0.9)";
          context.shadowBlur = 20;
          context.strokeStyle = `rgba(250, 252, 255, ${(1 - elapsed / BOLT_MS).toFixed(3)})`;
          [points, ...branches].forEach((line, index) => {
            context.lineWidth = index === 0 ? 3.4 : 1.7;
            context.beginPath();
            line.forEach(([pointX, pointY], pointIndex) => {
              if (pointIndex === 0) context.moveTo(pointX, pointY);
              else context.lineTo(pointX, pointY);
            });
            context.stroke();
          });
          context.shadowBlur = 0;
        }
        frame = requestAnimationFrame(draw);
      };
      frame = requestAnimationFrame(draw);
    });
    return () => {
      unsubscribe();
      cancelAnimationFrame(frame);
    };
  }, []);

  /* Render */
  const render = () => (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[100] h-full w-full"
    />
  );

  return render();
};
